/**
 * @fileoverview Ultra-reactive mobile frontend component for stadium fans playing "Reacción Rápida 1 vs 1" (Quick Reaction).
 * Provides low-latency touch interaction with 100dvh pointer-events tap surface, zero 300ms click lag,
 * native Web Audio synthesis (zero MP3s), Screen Wake Lock management with auto-reacquisition on visibilitychange,
 * native haptic vibration feedback, robust multi-room SignalR synchronization, and graceful handling of the 'kicked_out' pattern.
 * 
 * @module components/Dashboard/Sections/QuickReactionFan
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    ConnectWebSocketForQuickReactionFan,
    DisconnectWebSocketForQuickReactionFan
} from '../../../utilities/WS/QuickReactionFanWS';
import SignalRService from '../../../utilities/WS/SignalRService';
import {
    GetConfigBL,
    SelectPlayerSideBL,
    SendTapBL,
    UnregisterFanBL,
    GetPlayerSessionData,
    SavePlayerSessionData,
    ClearPlayerSessionData,
    UnlockAudioContext,
    SynthesizeQuickReactionSound,
    RequestScreenWakeLock,
    ReleaseScreenWakeLock,
    TriggerHapticFeedback,
    SanitizeRoomId,
    SanitizePlayerName,
    SanitizeText,
    SanitizeAssetUrl
} from '../../../utilities/BL/QuickReaction';

/**
 * Normaliza y resuelve la URL absoluta de un recurso multimedia multimedia del tema.
 * Previene errores de protocolo y traversal asegurando compatibilidad con el backend de media.
 * 
 * @param {string} mediaPath - Ruta relativa o URL absoluta del recurso.
 * @returns {string} URL formateada completa y saneada.
 */
export const getFullAssetUrl = (mediaPath) => {
    if (!mediaPath) return '';
    const sanitizedPath = SanitizeAssetUrl(mediaPath);
    if (!sanitizedPath) return '';

    if (
        sanitizedPath.startsWith('http://') ||
        sanitizedPath.startsWith('https://') ||
        sanitizedPath.startsWith('blob:') ||
        sanitizedPath.startsWith('data:')
    ) {
        return sanitizedPath;
    }

    const rawApiUrl = process.env.REACT_APP_API_URL || 'https://localhost:7205/api/';
    const baseOrigin = rawApiUrl.replace(/\/api\/?$/i, '').replace(/\/+$/, '');
    const cleanPath = sanitizedPath.startsWith('/') ? sanitizedPath : `/${sanitizedPath}`;
    return `${baseOrigin}${cleanPath}`;
};

/**
 * Constantes de colores por defecto para Lado 1 (Azul) y Lado 2 (Rojo).
 * @constant {Object}
 */
const DEFAULT_THEME_COLORS = {
    player1Color: '#00d2ff',
    player1Accent: '#0072ff',
    player2Color: '#ff416c',
    player2Accent: '#ff4b2b',
    fanBackgroundColor: '#0d0e15',
    fanTextColor: '#ffffff'
};

/**
 * Clave de sessionStorage para recordar la preferencia de silencio del usuario.
 * @constant {string}
 */
const SESSION_STORAGE_MUTE_KEY = 'quick_reaction_fan_muted';

/**
 * Componente móvil ultra reactivo para el Aficionado en 'Reacción Rápida 1 vs 1'.
 * 
 * @function QuickReactionFan
 * @param {Object} [props] - Propiedades del componente.
 * @param {Object} [props.matchState] - Estado inicial de partida pasado por router o Sections.js.
 * @param {Object} [props.config] - Configuración inicial del tema.
 * @param {string} [props.winner] - Ganador inicial en caso de reconexión.
 * @param {string} [props.roomId] - Identificador GUID de la sala.
 * @returns {React.ReactElement} Interfaz táctil reactiva a pantalla completa (100dvh).
 */
export const QuickReactionFan = ({
    matchState: propMatchState,
    config: propConfig,
    winner: propWinner,
    roomId: propRoomId,
    playerSide: propPlayerSide,
    side: propSide
}) => {
    // =========================================================================
    // 1. DETECCIÓN Y SANEAMIENTO DE SALA
    // =========================================================================
    const searchParams = useMemo(() => {
        return typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    }, []);

    const activeRoomId = useMemo(() => {
        const rawId = (
            propRoomId ||
            searchParams.get('roomId') ||
            propMatchState?.roomId ||
            (typeof localStorage !== 'undefined' ? localStorage.getItem('active_room_id') : '') ||
            ''
        );
        return SanitizeRoomId(rawId);
    }, [propRoomId, searchParams, propMatchState]);

    // =========================================================================
    // 2. RECUPERACIÓN DE SESIÓN PERSISTENTE DE JUGADOR (PATRÓN TACOS)
    // =========================================================================
    const initialSession = useMemo(() => {
        return GetPlayerSessionData(activeRoomId) || null;
    }, [activeRoomId]);

    const [selectedPlayerSide, setSelectedPlayerSide] = useState(() => {
        const directProp = propPlayerSide || propSide || propMatchState?.playerSide || propMatchState?.side;
        if (directProp) {
            const rawSide = String(directProp).toLowerCase();
            return (rawSide === 'player1' || rawSide === 'j1' || rawSide === 'p1' || rawSide === '1' || rawSide === 'left')
                ? 'player1'
                : 'player2';
        }
        if (initialSession?.side) {
            const rawSide = String(initialSession.side).toLowerCase();
            return (rawSide === 'player1' || rawSide === 'j1' || rawSide === 'p1' || rawSide === '1' || rawSide === 'left')
                ? 'player1'
                : 'player2';
        }
        return '';
    });

    const [playerNameInput, setPlayerNameInput] = useState(() => {
        return initialSession?.playerName ? SanitizePlayerName(initialSession.playerName) : 'Aficionado';
    });

    // =========================================================================
    // 3. ESTADOS DE LA MÁQUINA DE ESTADOS DE CANCHA
    // =========================================================================
    // Fases: 'SELECTION' | 'WAITING' | 'COUNTDOWN' | 'RANDOM_WAIT' | 'TRIGGER_ACTIVE' | 'FINISHED'
    const [gamePhase, setGamePhase] = useState(() => {
        const side = initialSession?.side || '';
        if (!side) return 'SELECTION';
        const rawP = propMatchState?.phase || propConfig?.phase || 'idle';
        const p = String(rawP).toLowerCase();
        if (p === 'countdown') return 'COUNTDOWN';
        if (p === 'random_wait' || p === 'randomwait') return 'RANDOM_WAIT';
        if (p === 'trigger_active' || p === 'triggeractive' || p === 'trigger') return 'TRIGGER_ACTIVE';
        if (p === 'finished' || p === 'game_over') return 'FINISHED';
        return 'WAITING';
    });

    const [countdownValue, setCountdownValue] = useState(3);
    const [localReactionTimeMs, setLocalReactionTimeMs] = useState(null);
    const [hasTappedThisRound, setHasTappedThisRound] = useState(false);
    const [isEarlyFaultCommitted, setIsEarlyFaultCommitted] = useState(false);

    // Estado del resultado final de la ronda
    const [roundResolution, setRoundResolution] = useState({
        winnerSide: null, // 'J1' | 'J2' | 'None' | 'Tie'
        winnerName: '',
        winReason: '', // 'FASTEST_TIME' | 'OPPONENT_FAULT' | 'TIMEOUT' | 'TIE' | 'ADMIN_FORCE'
        isWinner: false,
        myReactionTimeMs: null,
        rivalReactionTimeMs: null,
        timeDifferenceMs: null
    });

    // Configuración del juego y tema activo
    const [themeData, setThemeData] = useState(() => {
        return propConfig || propMatchState?.theme || null;
    });

    const [gamePhysicsConfig, setGamePhysicsConfig] = useState(() => ({
        enableEarlyFault: Boolean(propConfig?.enableEarlyFault ?? propMatchState?.enableEarlyFault ?? false),
        enableSpamCooldown: Boolean(propConfig?.enableSpamCooldown ?? propMatchState?.enableSpamCooldown ?? false),
        spamCooldownMs: propConfig?.spamCooldownMs || propMatchState?.spamCooldownMs || 300,
        timingMode: propConfig?.timingMode || propMatchState?.timingMode || 'ServerArrival'
    }));

    useEffect(() => {
        if (propMatchState?.phase) {
            const p = String(propMatchState.phase).toLowerCase();
            if (p === 'countdown') setGamePhase('COUNTDOWN');
            else if (p === 'random_wait' || p === 'randomwait') setGamePhase('RANDOM_WAIT');
            else if (p === 'trigger_active' || p === 'triggeractive' || p === 'trigger') setGamePhase('TRIGGER_ACTIVE');
            else if (p === 'finished' || p === 'game_over') setGamePhase('FINISHED');
            else if (p === 'waiting' || p === 'idle' || p === 'game_ready') setGamePhase(selectedPlayerSide ? 'WAITING' : 'SELECTION');
        }
    }, [propMatchState?.phase, selectedPlayerSide]);

    useEffect(() => {
        if (propConfig) {
            setGamePhysicsConfig((prev) => ({
                ...prev,
                enableEarlyFault: propConfig.enableEarlyFault !== undefined ? Boolean(propConfig.enableEarlyFault) : prev.enableEarlyFault,
                enableSpamCooldown: propConfig.enableSpamCooldown !== undefined ? Boolean(propConfig.enableSpamCooldown) : prev.enableSpamCooldown,
                spamCooldownMs: propConfig.spamCooldownMs || prev.spamCooldownMs,
                timingMode: propConfig.timingMode || prev.timingMode
            }));
        }
    }, [propConfig]);

    // Estados de control de audio y modales
    const [isMuted, setIsMuted] = useState(() => {
        if (typeof sessionStorage !== 'undefined') {
            return sessionStorage.getItem(SESSION_STORAGE_MUTE_KEY) === 'true';
        }
        return false;
    });

    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [toastMessage, setToastMessage] = useState('');
    const [showKickedOutModal, setShowKickedOutModal] = useState(false);
    const [kickedOutReasonMessage, setKickedOutReasonMessage] = useState('');

    // =========================================================================
    // 4. REFERENCIAS MUTABLES PARA TIEMPOS PRECISOS, ANTI-SPAM Y CICLO DE VIDA ESTABLE
    // =========================================================================
    const triggerLocalStartTimestampRef = useRef(null);
    const triggerServerTimestampRef = useRef(null);
    const fullScreenTriggerSurfaceRef = useRef(null);
    const wakeLockActiveRef = useRef(false);
    const hasTappedThisRoundRef = useRef(false);
    const lastTapTimestampRef = useRef(0);

    // Refs de estado para evitar re-suscripciones innecesarias de WebSocket
    const isMutedRef = useRef(isMuted);
    useEffect(() => {
        isMutedRef.current = isMuted;
    }, [isMuted]);

    const localReactionTimeMsRef = useRef(localReactionTimeMs);
    useEffect(() => {
        localReactionTimeMsRef.current = localReactionTimeMs;
    }, [localReactionTimeMs]);

    const selectedPlayerSideRef = useRef(selectedPlayerSide);
    useEffect(() => {
        selectedPlayerSideRef.current = selectedPlayerSide;
    }, [selectedPlayerSide]);

    const gamePhaseRef = useRef(gamePhase);
    useEffect(() => {
        gamePhaseRef.current = gamePhase;
    }, [gamePhase]);

    const gamePhysicsConfigRef = useRef(gamePhysicsConfig);
    useEffect(() => {
        gamePhysicsConfigRef.current = gamePhysicsConfig;
    }, [gamePhysicsConfig]);

    /**
     * Muestra un mensaje flotante tipo Toast temporal saneado.
     * @param {string} msg - Texto a mostrar.
     * @param {number} [duration=3000] - Duración en milisegundos.
     */
    const showToast = useCallback((msg, duration = 3000) => {
        const cleanMsg = SanitizeText(msg, 150);
        setToastMessage(cleanMsg);
        setTimeout(() => setToastMessage(''), duration);
    }, []);

    // =========================================================================
    // 5. GESTIÓN DEL SCREEN WAKE LOCK API (AUTO-REQUISICIÓN Y LIBERACIÓN)
    // =========================================================================
    const acquireWakeLock = useCallback(async () => {
        try {
            const acquired = await RequestScreenWakeLock();
            if (acquired) {
                wakeLockActiveRef.current = true;
            }
        } catch {
            wakeLockActiveRef.current = false;
        }
    }, []);

    const releaseWakeLock = useCallback(async () => {
        try {
            await ReleaseScreenWakeLock();
            wakeLockActiveRef.current = false;
        } catch {
            wakeLockActiveRef.current = false;
        }
    }, []);

    useEffect(() => {
        if (selectedPlayerSide && gamePhase !== 'FINISHED' && gamePhase !== 'SELECTION') {
            acquireWakeLock();
        } else if (gamePhase === 'FINISHED') {
            releaseWakeLock();
        }
    }, [selectedPlayerSide, gamePhase, acquireWakeLock, releaseWakeLock]);

    useEffect(() => {
        return () => {
            releaseWakeLock();
        };
    }, [releaseWakeLock]);

    // =========================================================================
    // 6. TOGGLE DE AUDIO Y MUTE
    // =========================================================================
    const toggleMute = useCallback(() => {
        setIsMuted((prev) => {
            const nextValue = !prev;
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem(SESSION_STORAGE_MUTE_KEY, String(nextValue));
            }
            if (!nextValue) {
                UnlockAudioContext();
            }
            return nextValue;
        });
    }, []);

    // =========================================================================
    // 7. CARGA DE CONFIGURACIÓN INICIAL DEL TEMA Y SALA
    // =========================================================================
    useEffect(() => {
        let isMounted = true;
        const fetchConfigAndTheme = async () => {
            try {
                const response = await GetConfigBL(activeRoomId || '');
                if (isMounted && response.success && response.data) {
                    const cfg = response.data;
                    const incomingTheme = cfg.activeTheme || cfg.ActiveTheme || cfg.theme;
                    if (incomingTheme) {
                        setThemeData(incomingTheme);
                    }
                    setGamePhysicsConfig((prev) => ({
                        ...prev,
                        enableEarlyFault: cfg.enableEarlyFault !== undefined ? Boolean(cfg.enableEarlyFault) : (propConfig?.enableEarlyFault !== undefined ? Boolean(propConfig.enableEarlyFault) : prev.enableEarlyFault),
                        enableSpamCooldown: cfg.enableSpamCooldown !== undefined ? Boolean(cfg.enableSpamCooldown) : (propConfig?.enableSpamCooldown !== undefined ? Boolean(propConfig.enableSpamCooldown) : prev.enableSpamCooldown),
                        spamCooldownMs: Number(cfg.spamCooldownMs) || propConfig?.spamCooldownMs || prev.spamCooldownMs,
                        timingMode: cfg.timingMode === 2 || cfg.timingMode === 'ClientOffsetSync'
                            ? 'ClientOffsetSync'
                            : (cfg.timingMode || propConfig?.timingMode || prev.timingMode)
                    }));
                }
            } catch (err) {
                console.warn('[QuickReactionFan] Error al consultar configuración inicial:', err);
            }
        };

        fetchConfigAndTheme();
        return () => {
            isMounted = false;
        };
    }, [activeRoomId, propConfig]);

    // Sincronizar props entrantes si cambian externamente
    useEffect(() => {
        if (propConfig && typeof propConfig === 'object') {
            const incomingTheme = propConfig.activeTheme || propConfig.ActiveTheme || propConfig.theme || propConfig;
            if (incomingTheme && (incomingTheme.screenBackgroundImageUrl || incomingTheme.mobileBackgroundImageUrl || incomingTheme.j1SpriteFrontUrl)) {
                setThemeData((prev) => ({ ...(prev || {}), ...incomingTheme }));
            }
            setGamePhysicsConfig((prev) => ({
                ...prev,
                enableEarlyFault: propConfig.enableEarlyFault !== undefined ? Boolean(propConfig.enableEarlyFault) : prev.enableEarlyFault,
                enableSpamCooldown: propConfig.enableSpamCooldown !== undefined ? Boolean(propConfig.enableSpamCooldown) : prev.enableSpamCooldown,
                spamCooldownMs: propConfig.spamCooldownMs || prev.spamCooldownMs,
                timingMode: propConfig.timingMode || prev.timingMode
            }));
        }
    }, [propConfig]);

    // =========================================================================
    // 8. MANEJO DEL PATRÓN KICKED_OUT (DESALOJO PACÍFICO)
    // =========================================================================
    const handleKickedOutEvent = useCallback((envelope) => {
        console.warn('[QuickReactionFan] Evento kicked_out recibido del servidor:', envelope);

        // 1. Limpiar almacenamiento de sesión
        ClearPlayerSessionData(activeRoomId);

        // 2. Liberar WakeLock
        releaseWakeLock();

        // 3. Limpiar estado de jugador de forma atómica
        hasTappedThisRoundRef.current = false;
        localReactionTimeMsRef.current = null;
        setSelectedPlayerSide('');
        setGamePhase('SELECTION');
        setIsEarlyFaultCommitted(false);
        setHasTappedThisRound(false);
        setLocalReactionTimeMs(null);

        // 4. Mostrar aviso amigable y saneado
        const rawMsg = envelope?.message || envelope?.payload?.message || 'Tu personaje ha sido reasignado a otro aficionado.';
        const cleanMsg = SanitizeText(rawMsg, 200);
        setKickedOutReasonMessage(cleanMsg);
        setShowKickedOutModal(true);
    }, [activeRoomId, releaseWakeLock]);

    // =========================================================================
    // 9. PROCESAMIENTO CENTRAL DE MENSAJES SIGNALR (REFERENCIAS ESTABLES)
    // =========================================================================
    const handleIncomingWebSocketMessage = useCallback((messageEnvelope) => {
        if (!messageEnvelope) return;

        const rawType = messageEnvelope.type || messageEnvelope.Type || '';
        const eventType = String(rawType).toLowerCase();
        const payload = messageEnvelope.payload || messageEnvelope.data || messageEnvelope;
        const currentSide = selectedPlayerSideRef.current;
        const muted = isMutedRef.current;

        // Extraer y aplicar tema si viene en cualquier mensaje o estado
        const incomingTheme = payload?.theme ||
            payload?.activeTheme ||
            payload?.ActiveTheme ||
            payload?.state?.theme ||
            payload?.state?.activeTheme ||
            payload?.state?.ActiveTheme ||
            messageEnvelope?.state?.theme ||
            messageEnvelope?.state?.activeTheme ||
            messageEnvelope?.state?.ActiveTheme ||
            messageEnvelope?.theme;

        if (incomingTheme && typeof incomingTheme === 'object') {
            setThemeData((prev) => ({ ...(prev || {}), ...incomingTheme }));
        }

        switch (eventType) {
            case 'kicked_out': {
                handleKickedOutEvent(messageEnvelope);
                break;
            }

            case 'quick_reaction_state':
            case 'quick_reaction_snapshot': {
                const snapState = payload.state || payload;
                if (snapState && typeof snapState === 'object') {
                    if (snapState.activeTheme) {
                        setThemeData(snapState.activeTheme);
                    }
                    if (snapState.enableEarlyFault !== undefined) {
                        setGamePhysicsConfig(prev => ({ ...prev, enableEarlyFault: Boolean(snapState.enableEarlyFault) }));
                    }
                }
                break;
            }

            case 'quick_reaction_cover_shown':
            case 'quick_reaction_cover': {
                if (incomingTheme) setThemeData(incomingTheme);
                if (currentSide) {
                    setGamePhase('WAITING');
                } else {
                    setGamePhase('SELECTION');
                }
                hasTappedThisRoundRef.current = false;
                setIsEarlyFaultCommitted(false);
                setHasTappedThisRound(false);
                localReactionTimeMsRef.current = null;
                setLocalReactionTimeMs(null);
                break;
            }

            case 'quick_reaction_game_scene_shown':
            case 'quick_reaction_game_shown':
            case 'quick_reaction_game_ready':
            case 'quick_reaction_reset': {
                if (incomingTheme) setThemeData(incomingTheme);
                if (payload.enableEarlyFault !== undefined) {
                    setGamePhysicsConfig(prev => ({ ...prev, enableEarlyFault: Boolean(payload.enableEarlyFault) }));
                }
                hasTappedThisRoundRef.current = false;
                setIsEarlyFaultCommitted(false);
                setHasTappedThisRound(false);
                localReactionTimeMsRef.current = null;
                setLocalReactionTimeMs(null);
                setRoundResolution({
                    winnerSide: null,
                    winnerName: '',
                    winReason: '',
                    isWinner: false,
                    myReactionTimeMs: null,
                    rivalReactionTimeMs: null,
                    timeDifferenceMs: null
                });

                if (currentSide) {
                    setGamePhase('WAITING');
                    acquireWakeLock();
                } else {
                    setGamePhase('SELECTION');
                }
                break;
            }

            case 'quick_reaction_countdown_tick':
            case 'quick_reaction_countdown': {
                const count = payload.count !== undefined ? payload.count
                    : (payload.secondsRemaining !== undefined ? payload.secondsRemaining : 3);
                setCountdownValue(count);

                if (currentSide) {
                    setGamePhase('COUNTDOWN');
                    acquireWakeLock();

                    // Tonos de audio sincronizados si no está en mute
                    if (!muted) {
                        if (count === 3) SynthesizeQuickReactionSound('COUNTDOWN_3');
                        else if (count === 2) SynthesizeQuickReactionSound('COUNTDOWN_2');
                        else if (count === 1) SynthesizeQuickReactionSound('COUNTDOWN_1');
                    }
                }
                break;
            }

            case 'quick_reaction_random_wait': {
                if (currentSide) {
                    setGamePhase('RANDOM_WAIT');
                    acquireWakeLock();

                    // Tono grave sub-bass de tensión si no está en mute
                    if (!muted) {
                        SynthesizeQuickReactionSound('TENSION');
                    }
                }
                break;
            }

            case 'quick_reaction_trigger_active': {
                if (currentSide) {
                    // Marcar inicio local y del servidor
                    triggerLocalStartTimestampRef.current = Date.now();
                    triggerServerTimestampRef.current = payload.triggerTimestampServerMs || Date.now();

                    hasTappedThisRoundRef.current = false;
                    setGamePhase('TRIGGER_ACTIVE');
                    setHasTappedThisRound(false);
                    acquireWakeLock();

                    // Vibración háptica instantánea de 40ms
                    TriggerHapticFeedback('trigger');

                    // Tono percusivo de disparo si no está en mute
                    if (!muted) {
                        SynthesizeQuickReactionSound('TRIGGER');
                    }
                }
                break;
            }

            case 'quick_reaction_early_fault': {
                const faultSide = payload.faultSide || payload.sideDisplay || '';
                const mySideDisplay = currentSide === 'player1' ? 'J1' : 'J2';
                const isMe = String(faultSide).toUpperCase() === mySideDisplay;

                if (isMe) {
                    setIsEarlyFaultCommitted(true);
                    if (!muted) SynthesizeQuickReactionSound('FAULT');
                    TriggerHapticFeedback('fault');
                }

                setGamePhase('FINISHED');
                releaseWakeLock();
                break;
            }

            case 'quick_reaction_match_finished': {
                setGamePhase('FINISHED');
                releaseWakeLock();

                const winnerRaw = payload.winner || payload.winnerSide || 'None';
                const winReason = payload.winReason || 'FASTEST_TIME';
                const mySideDisplay = currentSide === 'player1' ? 'J1' : 'J2';

                const isWinner = String(winnerRaw).toUpperCase() === mySideDisplay;

                // Extraer tiempos de reacción
                const stateData = payload.state || {};
                const p1Time = payload.player1TimeMs ?? stateData.player1ReactionTimeMs ?? stateData.Player1ReactionTimeMs ?? stateData.player1?.reactionTimeMs ?? null;
                const p2Time = payload.player2TimeMs ?? stateData.player2ReactionTimeMs ?? stateData.Player2ReactionTimeMs ?? stateData.player2?.reactionTimeMs ?? null;

                const myTime = (currentSide === 'player1' ? p1Time : p2Time) || localReactionTimeMsRef.current || null;
                const rivalTime = (currentSide === 'player1' ? p2Time : p1Time) || null;
                let diffMs = null;
                if (myTime !== null && rivalTime !== null) {
                    diffMs = Math.abs(myTime - rivalTime);
                }

                setRoundResolution({
                    winnerSide: winnerRaw,
                    winnerName: payload.winnerName ? SanitizePlayerName(payload.winnerName) : '',
                    winReason: winReason,
                    isWinner: isWinner,
                    myReactionTimeMs: myTime,
                    rivalReactionTimeMs: rivalTime,
                    timeDifferenceMs: diffMs
                });

                // Audio y vibración de victoria o fin de ronda
                if (isWinner) {
                    if (!muted) SynthesizeQuickReactionSound('VICTORY');
                    TriggerHapticFeedback('victory');
                }
                break;
            }

            case 'quick_reaction_players_updated': {
                const stateData = payload.state || payload;
                if (stateData.displayMode) {
                    setGamePhysicsConfig(prev => ({ ...prev, displayMode: stateData.displayMode }));
                }
                break;
            }

            default:
                break;
        }
    }, [
        acquireWakeLock,
        releaseWakeLock,
        handleKickedOutEvent
    ]);

    // =========================================================================
    // 10. CICLO DE VIDA DE CONEXIÓN SIGNALR CON EL ROL DE JUGADOR
    // =========================================================================
    useEffect(() => {
        if (!selectedPlayerSide) {
            DisconnectWebSocketForQuickReactionFan();
            return;
        }

        ConnectWebSocketForQuickReactionFan(
            handleIncomingWebSocketMessage,
            setConnectionStatus,
            activeRoomId,
            selectedPlayerSide,
            handleKickedOutEvent
        );

        return () => {
            DisconnectWebSocketForQuickReactionFan();
            releaseWakeLock();
        };
    }, [selectedPlayerSide, activeRoomId, handleIncomingWebSocketMessage, handleKickedOutEvent, releaseWakeLock]);

    // Sincronización automática de ConnectionId en cuanto SignalR se conecta
    useEffect(() => {
        if (connectionStatus === 'connected' && selectedPlayerSide && activeRoomId) {
            const liveConnId = SignalRService.getConnectionId();
            if (liveConnId) {
                const cleanName = SanitizePlayerName(playerNameInput) || (selectedPlayerSide === 'player1' ? 'Jugador 1' : 'Jugador 2');
                SelectPlayerSideBL(activeRoomId, selectedPlayerSide, cleanName, liveConnId).catch(() => {});
            }
        }
    }, [connectionStatus, selectedPlayerSide, activeRoomId, playerNameInput]);

    // =========================================================================
    // 11. ACCIÓN: SELECCIÓN DE PERSONAJE / BANDO (LADO A VS LADO B)
    // =========================================================================
    const handlePickPlayerSide = async (sideChosen) => {
        // 1. Desbloquear Web Audio Context
        await UnlockAudioContext();

        // 2. Solicitar Screen Wake Lock
        await acquireWakeLock();

        const cleanName = SanitizePlayerName(playerNameInput) || (sideChosen === 'player1' ? 'Jugador 1' : 'Jugador 2');
        hasTappedThisRoundRef.current = false;
        setSelectedPlayerSide(sideChosen);
        setGamePhase('WAITING');
        setIsEarlyFaultCommitted(false);
        setHasTappedThisRound(false);
        localReactionTimeMsRef.current = null;
        setLocalReactionTimeMs(null);

        // 3. Persistir en sessionStorage
        SavePlayerSessionData(activeRoomId, {
            side: sideChosen,
            playerName: cleanName
        });

        // 4. Emitir registro al servidor vía BL / REST / SignalR
        try {
            const activeConnId = SignalRService.getConnectionId() || '';
            await SelectPlayerSideBL(activeRoomId, sideChosen, cleanName, activeConnId);
            showToast(`¡Bando ${sideChosen === 'player1' ? 'J1 (Azul)' : 'J2 (Rojo)'} seleccionado!`);
        } catch (err) {
            console.warn('[QuickReactionFan] Error al registrar selección de bando en servidor:', err);
        }
    };

    // =========================================================================
    // 12. ACCIÓN: SALIR / CAMBIAR DE PERSONAJE
    // =========================================================================
    const handleLeaveSide = async () => {
        try {
            const activeConnId = SignalRService.getConnectionId() || '';
            await UnregisterFanBL(activeRoomId, activeConnId);
        } catch (err) {
            console.warn('[QuickReactionFan] Error al desvincular aficionado:', err);
        }

        ClearPlayerSessionData(activeRoomId);
        DisconnectWebSocketForQuickReactionFan();
        releaseWakeLock();

        hasTappedThisRoundRef.current = false;
        localReactionTimeMsRef.current = null;
        setSelectedPlayerSide('');
        setGamePhase('SELECTION');
        setIsEarlyFaultCommitted(false);
        setHasTappedThisRound(false);
        setLocalReactionTimeMs(null);
    };

    // =========================================================================
    // 13. CONTROLADOR DE PULSACIÓN EN EL GATILLO CON PROTECCIÓN ATÓMICA ANTI-ABUSO
    // =========================================================================
    const handleTriggerPointerDown = useCallback((event) => {
        // Evitar comportamientos por defecto como zoom o selección
        if (event && event.preventDefault && event.cancelable) {
            event.preventDefault();
        }

        // Validación de evento confiable en producción (Anti-Bot / Script injection)
        if (process.env.NODE_ENV === 'production' && event && event.isTrusted === false) {
            console.warn('[QuickReactionFan] Evento de pulsación bloqueado por no ser confiable (isTrusted === false).');
            return;
        }

        const currentPhase = String(gamePhaseRef.current || gamePhase || propMatchState?.phase || '').toUpperCase();
        const currentSide = selectedPlayerSideRef.current || selectedPlayerSide || initialSession?.side || 'player1';
        const isEarlyFaultEnabled = Boolean(
            gamePhysicsConfigRef.current?.enableEarlyFault ??
            gamePhysicsConfig?.enableEarlyFault ??
            propConfig?.enableEarlyFault ??
            propMatchState?.enableEarlyFault
        );

        const now = Date.now();

        // Enfriamiento anti-spam local
        if (gamePhysicsConfigRef.current?.enableSpamCooldown) {
            const cooldown = gamePhysicsConfigRef.current.spamCooldownMs || 300;
            if (now - lastTapTimestampRef.current < cooldown) {
                return;
            }
            lastTapTimestampRef.current = now;
        }

        // CASO A: Pulsación durante la espera aleatoria (RANDOM_WAIT)
        if (currentPhase.includes('RANDOM_WAIT') || currentPhase.includes('RANDOMWAIT')) {
            if (!isEarlyFaultEnabled) {
                // Si la falta temprana está deshabilitada, ignorar silenciosamente sin penalizar
                return;
            }

            // Bloqueo sincrónico para evitar pulsaciones múltiples concurrentes
            if (hasTappedThisRoundRef.current) return;
            hasTappedThisRoundRef.current = true;

            // Falta anticipada cometida
            setIsEarlyFaultCommitted(true);
            setHasTappedThisRound(true);
            TriggerHapticFeedback('fault');
            if (!isMutedRef.current) SynthesizeQuickReactionSound('FAULT');

            const activeConnId = SignalRService.getConnectionId() || '';
            SendTapBL(activeRoomId, activeConnId, 0, currentSide).catch(() => {});
            return;
        }

        // CASO B: Pulsación durante la señal de disparo activa (TRIGGER_ACTIVE)
        if (currentPhase.includes('TRIGGER')) {
            // Bloqueo atómico sincrónico para evitar multi-taps concurrentes o de múltiples dedos
            if (hasTappedThisRoundRef.current) return;
            hasTappedThisRoundRef.current = true;

            setHasTappedThisRound(true);

            // 1. Vibración háptica instantánea de 20ms (cero latencia)
            TriggerHapticFeedback('tap');

            // 2. Cálculo inmediato del tiempo de reacción local en ms acotado de forma segura
            const startLocal = triggerLocalStartTimestampRef.current || now;
            const rawMs = now - startLocal;
            const calculatedMs = Math.max(1, Math.min(30000, Number.isFinite(rawMs) ? rawMs : 1));
            localReactionTimeMsRef.current = calculatedMs;
            setLocalReactionTimeMs(calculatedMs);

            // 3. Envío ultra rápido al backend autoritativo con ConnectionId y Side
            const activeConnId = SignalRService.getConnectionId() || '';
            SendTapBL(activeRoomId, activeConnId, calculatedMs, currentSide).catch((err) => {
                console.error('[QuickReactionFan] Error al enviar pulsación:', err);
            });
        }
    }, [
        activeRoomId,
        gamePhase,
        selectedPlayerSide,
        gamePhysicsConfig,
        propConfig,
        propMatchState,
        initialSession
    ]);

    // =========================================================================
    // 14. RESOLUCIÓN DE SPRITES Y FONDOS SEGÚN EL BANDO Y FASE DEL JUEGO
    // =========================================================================
    const isPlayer1 = selectedPlayerSide === 'player1';

    // Sprites del personaje según fase
    const currentSpriteUrl = useMemo(() => {
        if (!selectedPlayerSide || !themeData) return null;

        if (isPlayer1) {
            if (gamePhase === 'RANDOM_WAIT') {
                return getFullAssetUrl(themeData.j1SpriteBackUrl || themeData.J1SpriteBackUrl);
            }
            if (gamePhase === 'TRIGGER_ACTIVE') {
                return getFullAssetUrl(
                    themeData.j1SpriteShootUrl || themeData.J1SpriteShootUrl ||
                    themeData.j1SpriteFrontUrl || themeData.J1SpriteFrontUrl
                );
            }
            if (gamePhase === 'FINISHED') {
                if (roundResolution.isWinner) {
                    return getFullAssetUrl(themeData.j1SpriteVictoryUrl || themeData.J1SpriteVictoryUrl);
                }
                return getFullAssetUrl(themeData.j1SpriteFrontUrl || themeData.J1SpriteFrontUrl);
            }
            // WAITING o COUNTDOWN
            return getFullAssetUrl(themeData.j1SpriteFrontUrl || themeData.J1SpriteFrontUrl);
        } else {
            // Jugador 2
            if (gamePhase === 'RANDOM_WAIT') {
                return getFullAssetUrl(themeData.j2SpriteBackUrl || themeData.J2SpriteBackUrl);
            }
            if (gamePhase === 'TRIGGER_ACTIVE') {
                return getFullAssetUrl(
                    themeData.j2SpriteShootUrl || themeData.J2SpriteShootUrl ||
                    themeData.j2SpriteFrontUrl || themeData.J2SpriteFrontUrl
                );
            }
            if (gamePhase === 'FINISHED') {
                if (roundResolution.isWinner) {
                    return getFullAssetUrl(themeData.j2SpriteVictoryUrl || themeData.J2SpriteVictoryUrl);
                }
                return getFullAssetUrl(themeData.j2SpriteFrontUrl || themeData.J2SpriteFrontUrl);
            }
            // WAITING o COUNTDOWN
            return getFullAssetUrl(themeData.j2SpriteFrontUrl || themeData.J2SpriteFrontUrl);
        }
    }, [selectedPlayerSide, themeData, isPlayer1, gamePhase, roundResolution.isWinner]);

    // Fondo del tema móvil
    const mobileBackgroundStyle = useMemo(() => {
        const bgUrl = themeData?.mobileBackgroundImageUrl || themeData?.MobileBackgroundImageUrl;
        if (bgUrl) {
            return {
                backgroundImage: `url(${getFullAssetUrl(bgUrl)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            };
        }
        return {
            backgroundColor: DEFAULT_THEME_COLORS.fanBackgroundColor
        };
    }, [themeData]);

    const headerLogoUrl = useMemo(() => {
        const logo = themeData?.logoImageUrl || themeData?.LogoImageUrl;
        return logo ? getFullAssetUrl(logo) : '/img/page/adsl-five.png';
    }, [themeData]);

    // =========================================================================
    // 15. RENDERIZADO: MODAL DE KICKED_OUT (PATRÓN TACOS)
    // =========================================================================
    const renderKickedOutModal = () => {
        if (!showKickedOutModal) return null;

        return (
            <div className="quick-reaction-fan__kicked-modal-overlay">
                <div className="quick-reaction-fan__kicked-modal-card">
                    <div className="quick-reaction-fan__kicked-modal-icon">
                        <i className="bi bi-exclamation-triangle-fill"></i>
                    </div>
                    <h3 className="quick-reaction-fan__kicked-modal-title">Personaje Reasignado</h3>
                    <p className="quick-reaction-fan__kicked-modal-text">
                        {kickedOutReasonMessage || 'Otro aficionado ha seleccionado este bando en la sala.'}
                    </p>
                    <button
                        className="btn btn-primary quick-reaction-fan__btn--kicked-ok w-100 fw-bold py-2"
                        onClick={() => {
                            setShowKickedOutModal(false);
                            setKickedOutReasonMessage('');
                        }}
                    >
                        Entendido, elegir bando
                    </button>
                </div>
            </div>
        );
    };

    // =========================================================================
    // 16. RENDERIZADO: BOTÓN FLOTANTE DE MUTE Y CABECERA
    // =========================================================================
    const renderHeader = () => {
        return (
            <header className="quick-reaction-fan__header">
                <div className="quick-reaction-fan__header-brand">
                    <img
                        src={headerLogoUrl}
                        alt="Logo"
                        className="quick-reaction-fan__header-logo"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                </div>

                <div className="quick-reaction-fan__header-actions">
                    {/* Botón Flotante de Mute */}
                    <button
                        type="button"
                        className={`quick-reaction-fan__mute-btn quick-reaction-fan__mute-button ${isMuted ? 'is-muted' : ''}`}
                        onClick={toggleMute}
                        title={isMuted ? 'Activar sonido' : 'Silenciar sonido'}
                        aria-label={isMuted ? 'Activar sonido' : 'Silenciar sonido'}
                    >
                        {isMuted ? '🔇' : '🔊'}
                    </button>

                    {/* Estado de conexión */}
                    <span className={`quick-reaction-fan__status-badge ${connectionStatus === 'connected' ? 'connected' : 'connecting'}`}>
                        {connectionStatus === 'connected' ? 'En Vivo' : 'Conectando...'}
                    </span>

                    {/* Botón Salir si ya eligió bando */}
                    {selectedPlayerSide && gamePhase !== 'TRIGGER_ACTIVE' && gamePhase !== 'COUNTDOWN' && (
                        <button
                            type="button"
                            className="quick-reaction-fan__exit-button quick-reaction-fan__btn-leave"
                            onClick={handleLeaveSide}
                            title="Cambiar bando"
                        >
                            <i className="bi bi-arrow-left-circle"></i> Salir
                        </button>
                    )}
                </div>
            </header>
        );
    };

    // =========================================================================
    // 17. RENDERIZADO: FASE 1 - SELECCIÓN DE BANDO (LADO A VS LADO B)
    // =========================================================================
    if (gamePhase === 'SELECTION' || (!selectedPlayerSide && (gamePhase === 'WAITING' || !gamePhase))) {
        const j1PreviewAvatar = getFullAssetUrl(themeData?.j1SpriteFrontUrl || themeData?.J1SpriteFrontUrl);
        const j2PreviewAvatar = getFullAssetUrl(themeData?.j2SpriteFrontUrl || themeData?.J2SpriteFrontUrl);

        return (
            <div className="quick-reaction-fan quick-reaction-fan--selection quick-reaction-fan__selection" style={mobileBackgroundStyle}>
                {renderHeader()}
                {renderKickedOutModal()}

                {toastMessage && (
                    <div className="quick-reaction-fan__toast">
                        {toastMessage}
                    </div>
                )}

                <main className="quick-reaction-fan__content quick-reaction-fan__content--selection">
                    <div className="quick-reaction-fan__hero-section">
                        <h1 className="quick-reaction-fan__title">El más veloz de Garibaldi</h1>
                        <p className="quick-reaction-fan__subtitle">Elige tu personaje:</p>
                    </div>

                    {/* <div className="quick-reaction-fan__player-input-container">
                        <label htmlFor="quick-reaction-player-name" className="quick-reaction-fan__input-label">
                            Tu Nombre o Alias:
                        </label>
                        <input
                            id="quick-reaction-player-name"
                            type="text"
                            className="quick-reaction-fan__player-input"
                            value={playerNameInput}
                            maxLength={30}
                            placeholder="Escribe tu alias..."
                            onChange={(e) => setPlayerNameInput(SanitizePlayerName(e.target.value))}
                        />
                    </div> */}

                    <div className="quick-reaction-fan__cards-container">
                        {/* Tarjeta Lado 1 (J1 - Azul) */}
                        <div
                            className="quick-reaction-fan__team-card quick-reaction-fan__team-card--p1"
                            onPointerDown={() => handlePickPlayerSide('player1')}
                            role="button"
                            tabIndex={0}
                        >
                            <div className="quick-reaction-fan__card-avatar-wrapper">
                                {j1PreviewAvatar ? (
                                    <img
                                        src={j1PreviewAvatar}
                                        alt="Jugador 1"
                                        className="quick-reaction-fan__card-avatar"
                                    />
                                ) : (
                                    <div className="quick-reaction-fan__card-avatar-placeholder p1">
                                        ⚡ J1
                                    </div>
                                )}
                            </div>
                            <div className="quick-reaction-fan__card-info">
                                {/* <span className="quick-reaction-fan__card-role">LADO A</span> */}
                                <h3 className="quick-reaction-fan__card-name">JUGADOR 1</h3>
                                <span className="quick-reaction-fan__card-color-badge p1">Azul</span>
                            </div>
                            <div className="quick-reaction-fan__card-action-icon">
                                <i className="bi bi-chevron-right"></i>
                            </div>
                        </div>

                        {/* Tarjeta Lado 2 (J2 - Rojo) */}
                        <div
                            className="quick-reaction-fan__team-card quick-reaction-fan__team-card--p2"
                            onPointerDown={() => handlePickPlayerSide('player2')}
                            role="button"
                            tabIndex={0}
                        >
                            <div className="quick-reaction-fan__card-avatar-wrapper">
                                {j2PreviewAvatar ? (
                                    <img
                                        src={j2PreviewAvatar}
                                        alt="Jugador 2"
                                        className="quick-reaction-fan__card-avatar"
                                    />
                                ) : (
                                    <div className="quick-reaction-fan__card-avatar-placeholder p2">
                                        🔥 J2
                                    </div>
                                )}
                            </div>
                            <div className="quick-reaction-fan__card-info">
                                {/* <span className="quick-reaction-fan__card-role">LADO B</span> */}
                                <h3 className="quick-reaction-fan__card-name">JUGADOR 2</h3>
                                <span className="quick-reaction-fan__card-color-badge p2">Rojo</span>
                            </div>
                            <div className="quick-reaction-fan__card-action-icon">
                                <i className="bi bi-chevron-right"></i>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    // =========================================================================
    // 18. RENDERIZADO: FASE 2 - SALA DE ESPERA (WAITING)
    // =========================================================================
    if (gamePhase === 'WAITING') {
        return (
            <div className={`quick-reaction-fan quick-reaction-fan--waiting ${isPlayer1 ? 'side-p1' : 'side-p2'}`} style={mobileBackgroundStyle}>
                {renderHeader()}
                {renderKickedOutModal()}

                {toastMessage && (
                    <div className="quick-reaction-fan__toast">
                        {toastMessage}
                    </div>
                )}

                <main className="quick-reaction-fan__content quick-reaction-fan__content--waiting">
                    <div className="quick-reaction-fan__waiting-badge">
                        {/* {isPlayer1 ? '⚡ LADO A (AZUL)' : '🔥 LADO B (ROJO)'} */}
                        {isPlayer1 ? '⚡ Jugador 1' : '🔥 Jugador 2'}
                    </div>

                    <div className="quick-reaction-fan__character-stage">
                        {currentSpriteUrl ? (
                            <img
                                src={currentSpriteUrl}
                                alt="Personaje"
                                className="quick-reaction-fan__character-image quick-reaction-fan__character-image--waiting"
                            />
                        ) : (
                            <div className={`quick-reaction-fan__character-fallback ${isPlayer1 ? 'p1' : 'p2'}`}>
                                <i className="bi bi-person-fill"></i>
                            </div>
                        )}
                        <div className="quick-reaction-fan__character-glow"></div>
                    </div>

                    <div className="quick-reaction-fan__player-callout">
                        <h2 className="quick-reaction-fan__player-title">{playerNameInput}</h2>
                        <p className="quick-reaction-fan__status-hint">
                            <span className="spinner-grow spinner-grow-sm text-warning me-2" role="status"></span>
                            Esperando a que inicie la partida...
                        </p>
                    </div>

                    {/* <div className="quick-reaction-fan__instructions-box">
                        <p className="mb-0">
                            <strong>Instrucciones:</strong> Cuando escuches el conteo, pon tu dedo sobre la pantalla. Al sonar el disparo, ¡toca inmediatamente con toda tu velocidad!
                        </p>
                    </div> */}
                </main>
            </div>
        );
    }

    // =========================================================================
    // 19. RENDERIZADO: FASE 3 - CONTEO REGRESIVO (COUNTDOWN 3..2..1)
    // =========================================================================
    if (gamePhase === 'COUNTDOWN') {
        return (
            <div className={`quick-reaction-fan quick-reaction-fan--countdown ${isPlayer1 ? 'side-p1' : 'side-p2'}`} style={mobileBackgroundStyle}>
                {renderHeader()}

                <main className="quick-reaction-fan__content quick-reaction-fan__content--countdown">
                    <div className="quick-reaction-fan__countdown-wrapper">
                        <div className="quick-reaction-fan__countdown-label">¡ATENCIÓN!</div>
                        <div key={countdownValue} className="quick-reaction-fan__countdown-number">
                            {countdownValue}
                        </div>
                        <div className="quick-reaction-fan__countdown-sublabel">Dedo listo para el disparo...</div>
                    </div>
                </main>
            </div>
        );
    }

    // =========================================================================
    // 20. RENDERIZADO: FASE 4 - ESPERA ALEATORIA (RANDOM_WAIT - TENSIÓN)
    // =========================================================================
    if (gamePhase === 'RANDOM_WAIT') {
        return (
            <div
                className={`quick-reaction-fan quick-reaction-fan--random-wait quick-reaction-fan__arena-surface ${isPlayer1 ? 'side-p1' : 'side-p2'}`}
                style={mobileBackgroundStyle}
                onPointerDown={handleTriggerPointerDown}
                onMouseDown={handleTriggerPointerDown}
                onTouchStart={handleTriggerPointerDown}
                onClick={handleTriggerPointerDown}
            >
                {renderHeader()}

                <main className="quick-reaction-fan__content quick-reaction-fan__content--random-wait">
                    <div className="quick-reaction-fan__tension-banner">
                        <i className="bi bi-shield-exclamation me-1"></i> ¡PREPÁRATE!
                    </div>

                    <div className="quick-reaction-fan__character-stage">
                        {currentSpriteUrl ? (
                            <img
                                src={currentSpriteUrl}
                                alt="Personaje de espaldas"
                                className="quick-reaction-fan__character-image quick-reaction-fan__character-image--back"
                            />
                        ) : (
                            <div className={`quick-reaction-fan__character-fallback ${isPlayer1 ? 'p1' : 'p2'}`}>
                                <i className="bi bi-person-fill"></i>
                            </div>
                        )}
                    </div>

                    <div className="quick-reaction-fan__tension-message">
                        <h2>¡Dedo en el gatillo!</h2>
                        <p className="text-warning fw-bold mb-0">¡NO TE ADELANTES O SERÁS DESCALIFICADO!</p>
                    </div>
                </main>
            </div>
        );
    }

    // =========================================================================
    // 21. RENDERIZADO: FASE 5 - GATILLO A PANTALLA COMPLETA (TRIGGER_ACTIVE 100dvh)
    // =========================================================================
    if (gamePhase === 'TRIGGER_ACTIVE') {
        return (
            <div
                ref={fullScreenTriggerSurfaceRef}
                className={`quick-reaction-fan quick-reaction-fan--trigger-active quick-reaction-fan__arena-surface ${isPlayer1 ? 'side-p1' : 'side-p2'} ${hasTappedThisRound ? 'is-tapped' : ''}`}
                style={mobileBackgroundStyle}
                onPointerDown={handleTriggerPointerDown}
                onMouseDown={handleTriggerPointerDown}
                onTouchStart={handleTriggerPointerDown}
                onClick={handleTriggerPointerDown}
            >
                {/* Capa de destello y resplandor verde neón a 60 FPS */}
                <div className="quick-reaction-fan__neon-glow-overlay"></div>

                <div className="quick-reaction-fan__trigger-surface-content">
                    <div className="quick-reaction-fan__trigger-alert">
                        <h1 className="quick-reaction-fan__trigger-fire-text">¡¡DISPARO!!</h1>
                        <p className="quick-reaction-fan__trigger-prompt">¡¡TOCA LA PANTALLA YA!!</p>
                    </div>

                    <div className="quick-reaction-fan__character-stage quick-reaction-fan__character-stage--active">
                        {currentSpriteUrl ? (
                            <img
                                src={currentSpriteUrl}
                                alt="Personaje disparando"
                                className="quick-reaction-fan__character-image quick-reaction-fan__character-image--shoot"
                            />
                        ) : (
                            <div className={`quick-reaction-fan__character-fallback ${isPlayer1 ? 'p1' : 'p2'}`}>
                                <i className="bi bi-lightning-fill"></i>
                            </div>
                        )}
                    </div>

                    {/* {localReactionTimeMs !== null && (
                        <div className="quick-reaction-fan__live-time-badge">
                            Tu tiempo: <strong className="text-warning">{localReactionTimeMs} ms</strong>
                        </div>
                    )} */}
                </div>
            </div>
        );
    }

    // =========================================================================
    // 22. RENDERIZADO: FASE 6 - RESOLUCIÓN DE LA RONDA (FINISHED)
    // =========================================================================
    if (gamePhase === 'FINISHED') {
        const isWinner = roundResolution.isWinner;
        const isFault = isEarlyFaultCommitted || roundResolution.winReason === 'OPPONENT_FAULT';
        const faultByMe = isEarlyFaultCommitted;

        return (
            <div
                className={`quick-reaction-fan quick-reaction-fan--finished ${isWinner ? 'is-winner' : 'is-defeated'} ${isPlayer1 ? 'side-p1' : 'side-p2'}`}
                style={mobileBackgroundStyle}
            >
                {renderHeader()}
                {renderKickedOutModal()}

                <main className="quick-reaction-fan__content quick-reaction-fan__content--finished">
                    {/* Cartel de resolución */}
                    {faultByMe ? (
                        <div className="quick-reaction-fan__result-banner result-fault">
                            <h2 className="quick-reaction-fan__result-heading text-danger">¡FALSO INICIO!</h2>
                            <p className="quick-reaction-fan__result-sub">Has presionado el botón antes de la señal oficial</p>
                        </div>
                    ) : isWinner ? (
                        <div className="quick-reaction-fan__result-banner result-victory">
                            <h2 className="quick-reaction-fan__result-heading text-success">¡GANASTE!</h2>
                            <p className="quick-reaction-fan__result-sub">¡Fuiste el más rápido!</p>
                        </div>
                    ) : (
                        <div className="quick-reaction-fan__result-banner result-defeat">
                            <h2 className="quick-reaction-fan__result-heading text-danger">¡PERDISTE!</h2>
                            <p className="quick-reaction-fan__result-sub">
                                {roundResolution.winReason === 'OPPONENT_FAULT'
                                    ? 'Victoria por falta del rival'
                                    : 'Tu rival reaccionó más rápido.'}
                            </p>
                        </div>
                    )}

                    {/* Personaje celebrando o en reposo */}
                    <div className="quick-reaction-fan__character-stage quick-reaction-fan__character-stage--finished">
                        {currentSpriteUrl ? (
                            <img
                                src={currentSpriteUrl}
                                alt="Personaje"
                                className={`quick-reaction-fan__character-image ${isWinner ? 'quick-reaction-fan__character-image--victory' : ''}`}
                            />
                        ) : (
                            <div className={`quick-reaction-fan__character-fallback ${isPlayer1 ? 'p1' : 'p2'}`}>
                                <i className={`bi ${isWinner ? 'bi-trophy-fill' : 'bi-x-circle-fill'}`}></i>
                            </div>
                        )}
                    </div>

                    {/* Panel de Tiempos y Telemetría */}
                    {/* <div className="quick-reaction-fan__metrics-card">
                        <div className="quick-reaction-fan__metrics-row">
                            <span className="quick-reaction-fan__metric-label">Tu Tiempo:</span>
                            <span className="quick-reaction-fan__metric-value">
                                {roundResolution.myReactionTimeMs !== null ? `${roundResolution.myReactionTimeMs} ms` : (localReactionTimeMs !== null ? `${localReactionTimeMs} ms` : '--')}
                            </span>
                        </div>

                        {roundResolution.rivalReactionTimeMs !== null && (
                            <div className="quick-reaction-fan__metrics-row">
                                <span className="quick-reaction-fan__metric-label">Rival:</span>
                                <span className="quick-reaction-fan__metric-value">
                                    {roundResolution.rivalReactionTimeMs} ms
                                </span>
                            </div>
                        )}

                        {roundResolution.timeDifferenceMs !== null && (
                            <div className="quick-reaction-fan__metrics-row difference">
                                <span className="quick-reaction-fan__metric-label">Diferencia:</span>
                                <span className="quick-reaction-fan__metric-value text-warning">
                                    {roundResolution.timeDifferenceMs} ms
                                </span>
                            </div>
                        )}
                    </div> */}

                    {/* Botones de acción post-partida */}
                    {/* <div className="quick-reaction-fan__post-actions">
                        <button
                            type="button"
                            className="btn btn-outline-light w-100 py-2 fw-bold"
                            onClick={handleLeaveSide}
                        >
                            <i className="bi bi-people-fill me-1"></i> Cambiar de Personaje
                        </button>
                    </div> */}
                </main>
            </div>
        );
    }

    return null;
};

export default QuickReactionFan;
